import { z } from 'zod';

const text = z.string().min(1);
const tick = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const positive = tick.min(1);
export const WorkingBodyIdSchema = z.string().regex(/^body:[a-zA-Z0-9._-]+$/);
export const CombatPositionSchema = z.strictObject({
  x: z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER),
  y: z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER),
  layer: tick,
});
export const BasicCombatActionSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('move'), destination: CombatPositionSchema }),
  z.strictObject({ type: z.literal('attack'), targetId: text }),
]);
export const TacticalActionSchema = z.discriminatedUnion('type', [
  ...BasicCombatActionSchema.options,
  z.strictObject({ type: z.literal('engage'), targetId: text }),
  z.strictObject({ type: z.literal('brace'), toward: CombatPositionSchema }),
  z.strictObject({ type: z.literal('lunge'), targetId: text }),
  z.strictObject({ type: z.literal('withdraw'), destination: CombatPositionSchema }),
  z.strictObject({ type: z.literal('retreat'), destination: CombatPositionSchema }),
  z.strictObject({ type: z.literal('hold') }),
  z.strictObject({ type: z.literal('consume'), itemId: text }),
]);
export const DangerTriggerSchema = z.enum(['enemy-sighted', 'companion-badly-hurt', 'pc-targeted', 'pc-wounded']);
export const TacticalPcCommandSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('act'), action: TacticalActionSchema }),
  z.strictObject({ type: z.literal('danger-options'), triggers: z.array(DangerTriggerSchema).max(4) }),
]);
export const CombatPcCommandSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('pause'), paused: z.boolean() }),
  z.strictObject({ type: z.literal('queue'), action: BasicCombatActionSchema }),
  z.strictObject({ type: z.literal('clear-queue') }),
  ...TacticalPcCommandSchema.options,
]);
export const EnemyBehaviourSchema = z.discriminatedUnion('kind', [
  // mixedBlows: every second blow is a short cut, too quick for a lunge.
  z.strictObject({ kind: z.literal('guard'), sightRange: tick.min(1), mixedBlows: z.literal(true).optional() }),
  z.strictObject({ kind: z.literal('thief'), sightRange: tick.min(1), escape: CombatPositionSchema }),
  // An earlier life's survivor: answers your blows with whatever they carry, as you would answer theirs.
  z.strictObject({ kind: z.literal('survivor'), sightRange: tick.min(1), mixedBlows: z.literal(true).optional() }),
  z.strictObject({ kind: z.literal('working-dead'), anchor: CombatPositionSchema, task: CombatPositionSchema, radius: tick.min(1), remainsId: text }),
]);
export const TacticalEventSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('action-started'), tick, actorId: text, action: TacticalActionSchema, resolvesTick: tick, recoveryUntil: tick }),
  z.strictObject({ type: z.literal('action-ended'), tick, actorId: text, action: TacticalActionSchema, outcome: z.enum(['resolved', 'blocked', 'interrupted', 'unavailable']), reason: text }),
  z.strictObject({ type: z.literal('intention'), tick, actorId: text, goal: text, reason: text, targetId: text.nullable() }),
  z.strictObject({ type: z.literal('stance'), tick, actorId: text, stance: z.enum(['holding', 'braced', 'mobile']), toward: CombatPositionSchema.nullable() }),
  z.strictObject({ type: z.literal('blocked'), tick, actorId: text, targetId: text, prevented: tick }),
  z.strictObject({ type: z.literal('consumed'), tick, actorId: text, itemId: text, healed: tick }),
  z.strictObject({ type: z.literal('danger'), tick, actorId: text, trigger: DangerTriggerSchema, targetId: text.nullable(), autoPaused: z.boolean() }),
]);
export const BasicCombatEventSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('moved'), tick, actorId: text, position: CombatPositionSchema }),
  z.strictObject({ type: z.literal('hit'), tick, actorId: text, targetId: text, damage: positive }),
  z.strictObject({ type: z.literal('interrupted'), tick, actorId: text, reason: z.enum(['damage', 'target-unavailable', 'out-of-range', 'no-line-of-sight']) }),
  z.strictObject({ type: z.literal('death'), tick, actorId: text }),
]);
export const CombatEventSchema = z.union([BasicCombatEventSchema, TacticalEventSchema]);

/** The playable save composes its identity, ledger and companion schemas here. */
export function createCombatStateSchema<
  PersonId extends z.ZodString,
  Continuity extends z.ZodType,
  Companion extends z.ZodType,
>(personId: PersonId, continuity: Continuity, companion: Companion) {
  const actorId = z.union([personId, WorkingBodyIdSchema]);
  const activeAction = z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal('move'), destination: CombatPositionSchema, route: z.array(z.strictObject({ position: CombatPositionSchema, cost: positive })).min(1), remainingTicks: positive }),
    z.strictObject({ type: z.literal('attack'), targetId: text, remainingTicks: positive }),
  ]);
  const actor = z.strictObject({
    id: actorId, role: z.enum(['pc', 'companion', 'enemy']), position: CombatPositionSchema,
    hp: tick, maxHp: positive, movePerTick: positive, attackDamage: positive,
    attackRange: positive, attackWindupTicks: positive, status: z.enum(['alive', 'dead']),
    activeAction: activeAction.nullable(),
  });
  const tacticalActor = z.strictObject({
    order: TacticalActionSchema.nullable(),
    commitment: z.strictObject({ action: TacticalActionSchema, startedTick: tick, resolvesTick: tick, recoveryUntil: tick }).nullable(),
    readyTick: tick, stance: z.enum(['holding', 'braced', 'mobile']), braceToward: CombatPositionSchema.nullable(),
    intention: z.string(), fleeing: z.boolean(), escaped: z.boolean(),
  });
  // Persisted saves omit the duplicated ledger (version 3 strips it); validation
  // re-attaches a clone of the life ledger, so the runtime shape is unchanged.
  const tactics = z.strictObject({
    version: z.literal('cb-combat-v1'), continuity: continuity.optional(),
    enemies: z.record(actorId, EnemyBehaviourSchema), companions: z.record(actorId, companion),
    actors: z.record(actorId, tacticalActor), dangerOptions: z.array(DangerTriggerSchema),
    sighted: z.array(actorId), badlyHurt: z.array(actorId), targetingPc: z.array(actorId),
  });
  return z.strictObject({
    tick, paused: z.boolean(), pcId: personId, actors: z.record(actorId, actor),
    pcQueue: z.array(BasicCombatActionSchema), lastPcSequence: z.number().int().min(-1).max(Number.MAX_SAFE_INTEGER),
    acceptedPcIntents: z.array(z.strictObject({ tick, sequence: tick, command: CombatPcCommandSchema })),
    events: z.array(CombatEventSchema), tactics: tactics.optional(),
  });
}

export type BasicCombatAction = z.infer<typeof BasicCombatActionSchema>;
export type CombatPcCommand = z.infer<typeof CombatPcCommandSchema>;
export type TacticalAction = z.infer<typeof TacticalActionSchema>;
export type TacticalPcCommand = z.infer<typeof TacticalPcCommandSchema>;
export type TacticalEvent = z.infer<typeof TacticalEventSchema>;
export type DangerTrigger = z.infer<typeof DangerTriggerSchema>;
export type EnemyBehaviour = z.infer<typeof EnemyBehaviourSchema>;
